import React, { createContext, useContext, useState, useEffect } from "react";
import { auth } from "../config/firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import authService from "../services/auth.service";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Función para generar token JWT
  const generateToken = async (firebaseUser) => {
    try {
      if (!firebaseUser) return null;
      
      // Obtener token de Firebase
      const firebaseToken = await firebaseUser.getIdToken();
      
      // Crear token JWT simple para el backend
      const tokenData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        firebaseToken: firebaseToken,
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (60 * 60 * 24) // 24 horas
      };
      
      // Codificar en base64 (simulación de JWT)
      const token = btoa(JSON.stringify(tokenData));
      
      // Guardar en localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('firebaseToken', firebaseToken);
      
      console.log('[AuthContext] Token generado y guardado');
      return token;
    } catch (error) {
      console.error('[AuthContext] Error generando token:', error);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        // Generar token cuando el usuario se autentica
        await generateToken(firebaseUser);
      } else {
        setUser(null);
        // Limpiar tokens cuando el usuario se desautentica
        localStorage.removeItem('token');
        localStorage.removeItem('firebaseToken');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      // Intentar login automático tras registro
      const userCredential = await signInWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );
      // Enviar email de verificación
      if (userCredential.user && !userCredential.user.emailVerified) {
        await sendEmailVerification(userCredential.user);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  };

  const registerWithGoogle = async (userData) => {
    try {
      const response = await authService.registerWithGoogle(userData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const loginWithGoogle = async (userData) => {
    try {
      const response = await authService.loginWithGoogle(userData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const checkEmailVerification = async (uid) => {
    try {
      const response = await authService.checkEmailVerification(uid);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      const response = await authService.resendVerificationEmail(email);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const sendWelcomeEmail = async (userEmail, userName, uid) => {
    try {
      const response = await authService.sendWelcomeEmail(
        userEmail,
        userName,
        uid
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const notifyOwnerNewRental = async (rentalData) => {
    try {
      const response = await authService.notifyOwnerNewRental(rentalData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const notifyDispute = async (disputeData) => {
    try {
      const response = await authService.notifyDispute(disputeData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const notifyRentalCancellation = async (cancellationData) => {
    try {
      const response = await authService.notifyRentalCancellation(
        cancellationData
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const notifyAdminReservationWithoutPayment = async (reservationData) => {
    try {
      const response = await authService.notifyAdminReservationWithoutPayment(
        reservationData
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const sendCustomEmail = async (emailData) => {
    try {
      const response = await authService.sendCustomEmail(emailData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    register,
    login,
    logout,
    resetPassword,
    loading,
    registerWithGoogle,
    loginWithGoogle,
    checkEmailVerification,
    resendVerificationEmail,
    sendWelcomeEmail,
    notifyOwnerNewRental,
    notifyDispute,
    notifyRentalCancellation,
    notifyAdminReservationWithoutPayment,
    sendCustomEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
